import React from 'react';
import type { ReactNode } from 'react';
import { Breakpoint, useBreakpoint } from '@box/blueprint-web';
import { MODE_FEATURES, EMAIL_ELEMENT } from '../../../../constants';
import { useModeFeature } from '../../../../common/contexts/mode-context/mode-context';
import { useFormSubmitterContext } from '../../form-submitter-context';
import SubmitterGroup from '../../../../common/components/group/submitter-group';
import FormElement from '..';
import css from './group-with-context.module.scss';
import {
  isItemInTwoColumn,
  isAtTwoColumnLeft,
  isAtTwoColumnRight,
} from '../../../new-file-request-builder/grid-layout-utils';

import type { GroupElement, Element } from '../../../../types/form/Element';

type Props = {
  description?: ReactNode;
  element: GroupElement;
};

const SingleColumnLayout = ({ components }: { components: Array<Element> }) =>
  components.map(component => (
    <FormElement key={component.id} element={component} />
  ));

type GridLayoutProps = {
  components: Array<Element>;
  layout: Array<{
    i: string;
    x: number;
    y: number;
  }>;
};
const GridLayout = ({ components, layout }: GridLayoutProps) => {
  const gridLayoutMap = new Map();
  const noLayoutComponents: Array<Element> = [];
  const sortedLayout = [...layout].sort((a, b) =>
    a.y === b.y ? a.x - b.x : a.y - b.y,
  );

  components.forEach(component => {
    const componentLayout = sortedLayout.find(item => item.i === component.id);
    if (componentLayout) {
      const row = gridLayoutMap.get(componentLayout.y) || [];
      row.push(component);
      gridLayoutMap.set(componentLayout.y, row);
    } else {
      noLayoutComponents.push(component);
    }
  });

  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
  const getEmptySlotComponent = (yPosition, pos: string) => ({
    id: `y-${yPosition}-empty-slot-${pos}`,
  });

  Array.from(gridLayoutMap).forEach(
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    ([yPosition, rowComponents]: [any, any]) => {
      // check for when there is only one component in the row
      if (rowComponents.length === 1) {
        const componentLayout = sortedLayout.find(
          item => item.i === rowComponents[0].id,
        );
        // add empty slot component to the right when half column field is on the left
        if (
          isItemInTwoColumn(componentLayout) &&
          isAtTwoColumnLeft(componentLayout)
        ) {
          rowComponents.push(getEmptySlotComponent(yPosition, 'on-right'));
        }
        // add empty slot component to the left when half column field is on the right
        if (
          isItemInTwoColumn(componentLayout) &&
          isAtTwoColumnRight(componentLayout)
        ) {
          rowComponents.unshift(getEmptySlotComponent(yPosition, 'on-left'));
        }
      }
    },
  );

  return (
    <>
      {Array.from(gridLayoutMap).map(
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        ([yPosition, rowComponents]: [any, any]) => (
          <div key={`form-row-y-${yPosition}`} className={css.formRow}>
            {/*
             // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006 */}
            {rowComponents.map(component => (
              <div
                key={component.id}
                className={css.formColumn}
                data-testid={`${component.id}-grid-item`}
              >
                {component.type && <FormElement element={component} />}
              </div>
            ))}
          </div>
        ),
      )}
      <SingleColumnLayout components={noLayoutComponents} />
    </>
  );
};

const GroupWithContext = (props: Props) => {
  const { element, description } = props;
  const { id, label, items = [] } = element;
  const {
    state,
    selector: { getComponentForId, getPageLayout },
  } = useFormSubmitterContext();
  const breakpointProvider = useBreakpoint();
  const isMobileScreen = breakpointProvider <= Breakpoint.Medium;
  const layout = getPageLayout(state);
  const { isAuthenticationRequired } = state;

  const useGridLayout =
    useModeFeature(MODE_FEATURES.TWO_COLUMN_LAYOUT) &&
    layout &&
    !isMobileScreen;

  const components = items
    .map(itemId => getComponentForId(state, itemId))
    .filter(component =>
      isAuthenticationRequired ? component.id !== EMAIL_ELEMENT : true,
    );

  return (
    <SubmitterGroup
      components={
        useGridLayout ? (
          <GridLayout components={components} layout={layout} />
        ) : (
          <SingleColumnLayout components={components} />
        )
      }
      description={description}
      id={id}
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
      inFocus={false}
      label={label}
    />
  );
};

export { GroupWithContext };
export default GroupWithContext;
